import React, { useState, useMemo, useCallback } from 'react'
import { useCQuery, invalidateQuery, FC } from '../../Services'
import { ServerDialog } from '../Agencies/ServerDialog'
import { DataGrid } from '@mui/x-data-grid'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import InputAdornment from '@mui/material/InputAdornment'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import DnsIcon from '@mui/icons-material/Dns'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

export const Servers = () => {
  const { data: servers = [], isSuccess: isSuccessServers } = useCQuery('servers')

  const [globalFilterValue, setGlobalFilterValue] = useState('')
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editDialogData, setEditDialogData] = useState(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [serverToDelete, setServerToDelete] = useState(null)

  const filteredServers = useMemo(() => {
    if (!globalFilterValue) return servers
    const lower = globalFilterValue.toLowerCase()
    return servers.filter((s) =>
      Object.values(s).some((v) => String(v).toLowerCase().includes(lower))
    )
  }, [servers, globalFilterValue])

  const handleEditServer = useCallback(async (serverData) => {
    if (!serverData?.name || !serverData?.ip || !serverData?.atlanteURL || !serverData?.SFTPURL || !serverData?.usernameSFTP || !serverData?.passwordSFTP) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Inserisci tutti i campi.' })
      return
    }

    const { _id: sId, ...serverFields } = serverData || {}
    await FC.service('servers').patch(sId, serverFields)
    invalidateQuery('servers')
    setEditDialogOpen(false)
  }, [])

  const handleCreateServer = useCallback(async (serverData) => {
    if (!serverData?.name || !serverData?.ip || !serverData?.atlanteURL || !serverData?.SFTPURL || !serverData?.usernameSFTP || !serverData?.passwordSFTP) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Inserisci tutti i campi.' })
      return
    }

    if (servers.some((item) => item.name.toUpperCase() === serverData?.name.toUpperCase() || item.ip === serverData?.ip)) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Esiste gia un server con lo stesso nome o IP.' })
      return
    }

    await FC.service('servers').create(serverData)
    invalidateQuery('servers')
    setCreateDialogOpen(false)
  }, [servers])

  const handleSaveServer = useCallback((serverData, isCreateMode) => {
    if (isCreateMode) {
      handleCreateServer(serverData)
    } else {
      handleEditServer(serverData)
    }
  }, [handleCreateServer, handleEditServer])

  const handleDeleteServer = useCallback(async () => {
    if (!serverToDelete) return
    try {
      await FC.service('servers').remove(serverToDelete._id)
      invalidateQuery('servers')
      window.growl.show({ severity: 'success', summary: 'Successo', detail: `Server "${serverToDelete.name}" eliminato.` })
    } catch {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Impossibile eliminare il server.' })
    }
    setDeleteConfirmOpen(false)
    setServerToDelete(null)
  }, [serverToDelete])

  const handleOpenDeleteConfirm = useCallback((server) => {
    setServerToDelete(server)
    setDeleteConfirmOpen(true)
  }, [])

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight={600} noWrap>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'ip',
      headerName: 'IP',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          size='small'
          variant='outlined'
          sx={{ fontWeight: 500, fontSize: 12, fontFamily: 'monospace' }}
        />
      )
    },
    {
      field: 'atlanteURL',
      headerName: 'Atlante URL',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant='body2' noWrap sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'SFTPURL',
      headerName: 'SFTP URL',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant='body2' noWrap sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'usernameSFTP',
      headerName: 'User SFTP',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant='body2' noWrap>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction='row' spacing={0.5} alignItems='center'>
          <Tooltip title='Modifica server'>
            <IconButton
              size='small'
              color='primary'
              onClick={() => { setEditDialogData({ serverId: params.row._id }); setEditDialogOpen(true) }}
              aria-label={`Modifica server ${params.row.name}`}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Elimina server'>
            <IconButton
              size='small'
              color='error'
              onClick={() => handleOpenDeleteConfirm(params.row)}
              aria-label={`Elimina server ${params.row.name}`}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ], [handleOpenDeleteConfirm])

  if (!isSuccessServers) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Stack alignItems='center' spacing={2}>
          <CircularProgress size={40} />
          <Typography variant='body2' color='text.secondary'>
            Caricamento server...
          </Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ mx: 'auto', width: '94%', maxWidth: 1600, pb: 5 }}>
      <ServerDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSaveServer}
        servers={servers}
        isSiapAdmin
        editData={editDialogData}
      />
      <ServerDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleSaveServer}
        servers={servers}
        isSiapAdmin
        editData={null}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setServerToDelete(null) }}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmberIcon color='warning' />
          <Typography variant='h6' component='span' fontWeight={600}>
            Conferma eliminazione
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            Sei sicuro di voler eliminare il server <strong>{serverToDelete?.name}</strong>?
            Questa azione non puo essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => { setDeleteConfirmOpen(false); setServerToDelete(null) }}
            variant='outlined'
            size='small'
            sx={{ textTransform: 'none' }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDeleteServer}
            variant='contained'
            color='error'
            size='small'
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2, mt: 1 }}
      >
        <Stack direction='row' alignItems='center' spacing={2}>
          <DnsIcon color='primary' sx={{ fontSize: 28 }} />
          <Typography variant='h5' fontWeight={700} color='text.primary'>
            Server FTP
          </Typography>
          <Chip
            label={`${servers.length} server`}
            size='small'
            variant='outlined'
            sx={{ fontWeight: 500 }}
          />
        </Stack>

        <Button
          variant='contained'
          size='small'
          onClick={() => setCreateDialogOpen(true)}
          startIcon={<AddIcon />}
          aria-label='Crea nuovo server'
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Nuovo Server
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: 1.5 }}
        >
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <SearchIcon fontSize='small' color='action' />
            <Typography variant='body2' fontWeight={600} color='text.secondary'>
              Ricerca
            </Typography>
          </Stack>
          <TextField
            size='small'
            value={globalFilterValue}
            onChange={(e) => setGlobalFilterValue(e.target.value)}
            placeholder='Cerca nei server...'
            aria-label='Ricerca server'
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                )
              }
            }}
            sx={{ width: 300 }}
          />
        </Stack>

        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={filteredServers}
            columns={columns}
            getRowId={(row) => row._id}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } }
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnMenu
            localeText={{
              noRowsLabel: 'Nessun server trovato.',
              MuiTablePagination: {
                labelRowsPerPage: 'Righe per pagina:',
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from}-${to} di ${count !== -1 ? count : `piu di ${to}`}`
              }
            }}
            sx={dataGridSx}
          />
        </Box>
      </Paper>
    </Box>
  )
}

const dataGridSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  '--DataGrid-containerBackground': '#004680',
  '& .MuiDataGrid-columnHeader': {
    color: '#fff',
    '&:focus, &:focus-within': { outline: 'none' }
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: 13,
    color: '#fff'
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    color: '#fff'
  },
  '& .MuiDataGrid-sortIcon': {
    color: '#fff'
  },
  '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root': {
    color: '#fff'
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
    color: 'text.primary',
    display: 'flex',
    alignItems: 'center',
    '&:focus, &:focus-within': { outline: 'none' }
  },
  '& .MuiDataGrid-row': {
    '&:hover': {
      backgroundColor: 'action.hover'
    }
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderTopColor: 'divider',
    backgroundColor: 'transparent'
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none'
  }
}
